import { useTranslation } from "react-i18next";
import { ITypeCarType, IUserType } from "common/src/types";
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { Box, Grid, Typography, Chip, CircularProgress, Card, CardContent, Tooltip, Paper } from "@mui/material";

interface CarSelectorProps {
    loading: boolean;
    cars: ITypeCarType[];
    selectedCar?: string;
    activeDrivers: IUserType[];
    onSelectCar: (car: ITypeCarType) => void;
}

export const CarSelector = ({
    cars,
    loading,
    selectedCar,
    onSelectCar,
    activeDrivers = []
}: CarSelectorProps) => {
    const { t } = useTranslation();

    const getCarAvailability = (car: ITypeCarType): { hasActiveDriver: boolean; driverCount: number; availableDrivers: IUserType[]; } => {
        const availableDrivers = activeDrivers.filter(driver =>
            driver.carType?.includes(car.name)
        );

        return {
            hasActiveDriver: availableDrivers.length > 0,
            driverCount: availableDrivers.length,
            availableDrivers
        };
    };

    const renderDriverTooltip = (drivers: IUserType[]) => {
        if (drivers.length === 0) {
            return <Typography variant="body2">{t("ADM_NO_DRIVERS_AVAILABLE")}</Typography>;
        }

        return (
            <Paper sx={{ p: 1, borderRadius: 2, overflowY: 'auto' }}>
                {drivers.map((driver, index) => (
                    <Box
                        key={driver.id || index}
                        sx={{
                            py: 1,
                            gap: 2,
                            display: 'flex',
                            alignItems: 'center',
                            borderColor: 'divider',
                        }}
                    >
                        {driver.profile_image ? (
                            <img
                                alt={driver.firstName}
                                src={driver.profile_image}
                                style={{
                                    width: 24,
                                    height: 24,
                                    objectFit: 'cover',
                                    borderRadius: '50%',
                                }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    width: 24,
                                    height: 24,
                                    display: 'flex',
                                    fontWeight: 'bold',
                                    borderRadius: '50%',
                                    fontSize: '0.75rem',
                                    alignItems: 'center',
                                    color: 'common.white',
                                    bgcolor: 'primary.main',
                                    justifyContent: 'center',
                                }}
                            >
                                {driver.firstName?.charAt(0) || 'D'}
                            </Box>
                        )}

                        <Typography
                            variant="body2"
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {driver.firstName} {driver.lastName || ''}
                        </Typography>
                    </Box>
                ))}
            </Paper>
        );
    };

    if (loading) {
        return (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!cars || cars.length === 0) {
        return (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography align="center" color="text.secondary" py={3}>
                    {t("ADM_NO_CARS_AVAILABLE")}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ height: "100%", p: 2, overflowY: "auto" }}>
            <Grid container spacing={2}>
                {cars.map((car) => {
                    const { hasActiveDriver, driverCount, availableDrivers } = getCarAvailability(car);
                    const isSelected = selectedCar === car.name;

                    return (
                        <Grid item xs={12} sm={6} md={4} key={car.id || car.name}>
                            <Tooltip
                                placement="top"
                                title={renderDriverTooltip(availableDrivers)}
                                disableHoverListener={!hasActiveDriver || availableDrivers.length === 0}
                            >
                                <Card
                                    onClick={() => {
                                        if (hasActiveDriver) {
                                            onSelectCar(car);
                                        }
                                    }}
                                    sx={{
                                        borderRadius: 4,
                                        position: 'relative',
                                        transition: 'all 0.2s',
                                        border: isSelected ? '2px solid' : '1px solid',
                                        cursor: hasActiveDriver ? 'pointer' : 'default',
                                        borderColor: isSelected ? 'primary.main' : 'divider',
                                        opacity: hasActiveDriver ? (isSelected ? 1 : 0.5) : 0.5,
                                        '&:hover': hasActiveDriver ? {
                                            opacity: 1,
                                            transform: 'scale(1.02)',
                                            boxShadow: 4,
                                        } : {},
                                    }}
                                >
                                    <CardContent>
                                        <Box
                                            sx={{
                                                flex: 1,
                                                minHeight: 0,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    height: '100px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                {car.image ? (
                                                    <img
                                                        src={car.image}
                                                        alt={car.name}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'contain'
                                                        }}
                                                    />
                                                ) : (
                                                    <DirectionsCarIcon sx={{ fontSize: 120, color: 'text.secondary' }} />
                                                )}
                                            </Box>

                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Typography variant="body2">
                                                    {car.name}
                                                </Typography>
                                            </Box>

                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                {t("ADM_MIN_FARE")}: ${car.min_fare}
                                            </Typography>

                                            <Box sx={{ mt: 1 }}>
                                                {hasActiveDriver ? (
                                                    <Chip
                                                        size="small"
                                                        color="success"
                                                        sx={{ fontSize: '0.7rem' }}
                                                        label={`${driverCount} ${t("driver")}${driverCount > 1 ? 's' : ''}`}
                                                    />
                                                ) : (
                                                    <Chip
                                                        size="small"
                                                        color="error"
                                                        label={t("not_available")}
                                                        sx={{ fontSize: '0.7rem' }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Tooltip>
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
};